<template>
  <v-card outlined>
    <v-card-text>
      <v-row v-if="effectiveShowShiftTotals">
        <v-col cols="12" sm="3">
          <v-text-field
            :value="shiftTotalsDisplay.total"
            label="CH Total"
            filled
            readonly
          />
        </v-col>
        <v-col cols="12" sm="3">
          <v-text-field
            v-if="editableShiftTotals"
            v-model="manualCh.morning"
            label="CH Manhã"
            filled
            :disabled="disabled"
            placeholder="auto"
            hint="Deixe vazio para calcular automaticamente"
            persistent-hint
          />
          <v-text-field
            v-else
            :value="shiftTotalsDisplay.morning"
            label="CH Manhã"
            filled
            readonly
          />
        </v-col>
        <v-col cols="12" sm="3">
          <v-text-field
            v-if="editableShiftTotals"
            v-model="manualCh.evening"
            label="CH Tarde"
            filled
            :disabled="disabled"
            placeholder="auto"
            hint="Deixe vazio para calcular automaticamente"
            persistent-hint
          />
          <v-text-field
            v-else
            :value="shiftTotalsDisplay.evening"
            label="CH Tarde"
            filled
            readonly
          />
        </v-col>
        <v-col cols="12" sm="3">
          <v-text-field
            v-if="editableShiftTotals"
            v-model="manualCh.night"
            label="CH Noite"
            filled
            :disabled="disabled"
            placeholder="auto"
            hint="Deixe vazio para calcular automaticamente"
            persistent-hint
          />
          <v-text-field
            v-else
            :value="shiftTotalsDisplay.night"
            label="CH Noite"
            filled
            readonly
          />
        </v-col>
      </v-row>

      <v-row>
        <v-col cols="12" sm="3">
          <v-select
            v-model="formModel.day_of_week_id"
            :items="daysOfWeekOptions"
            item-text="text"
            item-value="id"
            label="Dia da semana"
            filled
            :disabled="disabled"
          />
        </v-col>

        <v-col cols="6" sm="2">
          <v-text-field
            v-model="formModel.hour_start"
            label="Início (HH:mm)"
            filled
            v-mask="'00:00'"
            placeholder="HH:mm"
            :disabled="disabled"
          />
        </v-col>

        <v-col cols="6" sm="2">
          <v-text-field
            v-model="formModel.hour_end"
            label="Fim (HH:mm)"
            filled
            v-mask="'00:00'"
            placeholder="HH:mm"
            :disabled="disabled"
          />
        </v-col>

        <v-col cols="12" sm="3">
          <v-select
            v-model="formModel.availability_type_id"
            :items="availabilityTypeOptions"
            item-text="text"
            item-value="id"
            label="Tipo"
            filled
            :disabled="disabled"
          />
        </v-col>

        <v-col cols="12" sm="2" class="d-flex align-center">
          <v-btn
            color="primary"
            class="mr-2"
            x-small
            @click="onAddOrUpdate"
            :disabled="disabled"
          >
            <v-icon left x-small>{{ isEditing ? 'mdi-content-save' : 'mdi-plus' }}</v-icon>
            {{ isEditing ? 'Atualizar' : 'Adicionar' }}
          </v-btn>
          <v-btn text x-small @click="onReset" v-if="isEditing">Cancelar</v-btn>
        </v-col>
      </v-row>

      <v-alert v-if="!orderedItems.length" dense outlined type="info" class="mb-3">
        Nenhuma faixa personalizada adicionada.
      </v-alert>

      <v-row v-if="effectiveShowByCampuses">
        <v-col cols="12">
          <v-select
            v-model="formModel.campuses"
            :items="campusOptions"
            item-text="text"
            item-value="id"
            label="Campi"
            filled
            multiple
            chips
            :disabled="disabled"
          />
          <small class="grey--text text--darken-1">
            Campi não particionam a validação de sobreposição; o bloqueio é sempre por dia.
          </small>
        </v-col>
      </v-row>

      <v-divider class="my-3" />

      <v-simple-table v-if="orderedItems.length">
        <template v-slot:default>
          <thead>
            <tr>
              <th class="text-left">Dia</th>
              <th class="text-left">Início</th>
              <th class="text-left">Fim</th>
              <th class="text-left">Tipo</th>
              <th class="text-left" v-if="effectiveShowByCampuses">Campi</th>
              <th class="text-right">Ações</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="item in orderedItems" :key="item.__index">
              <td>{{ (daysOfWeekOptions.find(d => d.id === item.day_of_week_id) || {}).text }}</td>
              <td>{{ item.hour_start }}</td>
              <td>{{ item.hour_end }}</td>
              <td>
                <span>
                  <v-icon small :color="getColorFn(item.availability_type_id)">{{ getIconFn(item.availability_type_id) }}</v-icon>
                  {{ (availabilityTypeOptions.find(t => t.id === item.availability_type_id) || {}).text }}
                </span>
              </td>
              <td v-if="effectiveShowByCampuses">
                {{ (item.campuses || []).map(id => (campusesEffective.find(c => c.id === id) || {}).initials).filter(Boolean).join(', ') }}
              </td>
              <td class="text-right">
                <v-btn icon small @click="edit(item.__index)"><v-icon>mdi-pencil</v-icon></v-btn>
                <v-btn icon small color="error" @click="remove(item.__index)"><v-icon>mdi-delete</v-icon></v-btn>
              </td>
            </tr>
          </tbody>
        </template>
      </v-simple-table>

       <v-divider class="my-3" v-if="effectiveShowSave" />

       <div class="text-right" v-if="effectiveShowSave">
         <v-btn color="primary" dark small @click="save" :disabled="disabled || saving">
           <v-icon left dark>save</v-icon>
           Salvar
         </v-btn>
       </div>

       <v-snackbar v-model="snackbar" :timeout="20000" :color="snackbarColor" top>
         {{ snackbarText }}
       </v-snackbar>
    </v-card-text>
  </v-card>
</template>

<script>
import { mapActions } from 'vuex'

export default {
  name: 'SysTeacherAvailabilityCustomRange',
  props: {
    autoload: {
      type: Boolean,
      default: true,
    },
    autoShowByCampuses: {
      type: Boolean,
      default: true,
    },
    value: {
      type: Array,
      default: () => [],
    },
    headers: {
      type: Array,
      default: () => [],
    },
    gridIntervalsByDay: {
      type: Object,
      default: () => ({}),
    },
    showByCampuses: {
      type: Boolean,
      default: false,
    },
    campuses: {
      type: Array,
      default: () => [],
    },
    teacher: {
      type: Object,
      default: () => null,
    },
    workTime: {
      type: Object,
      default: () => null,
    },
    tokenTeacher: {
      type: [String, null],
      default: null,
    },
    token: {
      type: [String, null],
      default: null,
    },
    teacherId: {
      type: [Number, String, null],
      default: null,
    },
    workTimeId: {
      type: [Number, String, null],
      default: null,
    },
    release: {
      type: Boolean,
      default: false,
    },
    isAvailabilityEnabled: {
      type: Boolean,
      default: true,
    },
    status: {
      type: Number,
      default: 2,
    },
    digitalAvailability: {
      type: [Number, String],
      default: 0,
    },
    showEnableActivityTime: {
      type: Boolean,
      default: false,
    },
    getColor: {
      type: Function,
      default: null,
    },
    getIcon: {
      type: Function,
      default: null,
    },

    // Quando usado como componente standalone (sem v-model/@input),
    // o botao Salvar aparece por padrao.
    // Quando controlado pela pagina (com @input), o Salvar fica oculto por padrao
    // para evitar sobrescrever selecoes ainda nao salvas na grade semanal.
    showSave: {
      type: [Boolean, null],
      default: null,
    },

    // Permite sobrescrever manualmente a CH por turno (manha/tarde/noite).
    // Quando vazio, o componente usa o calculo automatico.
    editableShiftTotals: {
      type: Boolean,
      default: false,
    },

    // Controla a exibicao dos campos de CH (total/manha/tarde/noite).
    // Por padrao fica oculto.
    showShiftTotals: {
      type: Boolean,
      default: false,
    },
  },
  data () {
    return {
      editingIndex: null,
      formModel: {
        day_of_week_id: null,
        hour_start: '',
        hour_end: '',
        availability_type_id: 1,
        campuses: [],
      },
      localItems: [],
      localTeacher: null,
      localWorkTime: null,
      localCampuses: [],
      manualCh: {
        morning: '',
        evening: '',
        night: '',
      },
      saving: false,
      snackbar: false,
      snackbarText: '',
      snackbarColor: 'success',
    }
  },
  mounted () {
    this.init()
  },
  watch: {
    value: {
      immediate: true,
      handler (newVal) {
        // No modo controlado, mantemos um espelho local para garantir reatividade imediata
        // (ex.: atualizar CH no mesmo tick ao remover/adicionar).
        if (!this.isControlledModel) {
          return
        }

        this.localItems = Array.isArray(newVal) ? [...newVal] : []
      },
    },

    tokenTeacher (newVal, oldVal) {
      if (!this.autoload) {
        return
      }

      if (!newVal || newVal === oldVal) {
        return
      }

      // se a tela ja esta fornecendo contexto, nao precisamos recarregar por token
      if (this.teacher && this.workTime) {
        return
      }

      this.loadByToken(newVal)
    },

    token (newVal, oldVal) {
      if (!this.autoload) {
        return
      }

      if (!newVal || newVal === oldVal) {
        return
      }

      if (this.teacher && this.workTime) {
        return
      }

      this.loadByToken(newVal)
    },

    teacherId (newVal, oldVal) {
      if (!this.autoload) {
        return
      }

      if (!newVal || newVal === oldVal) {
        return
      }

      if (this.teacher && this.workTime) {
        return
      }

      if (this.workTimeId) {
        this.loadByIds(newVal, this.workTimeId)
      }
    },

    workTimeId (newVal, oldVal) {
      if (!this.autoload) {
        return
      }

      if (!newVal || newVal === oldVal) {
        return
      }

      if (this.teacher && this.workTime) {
        return
      }

      if (this.teacherId) {
        this.loadByIds(this.teacherId, newVal)
      }
    },
  },
  computed: {
    isControlledModel () {
      return !!(this.$listeners && this.$listeners.input)
    },

    effectiveShowSave () {
      if (this.showSave !== null && this.showSave !== undefined) {
        return !!this.showSave
      }
      return !this.isControlledModel
    },

    effectiveShowShiftTotals () {
      return !!this.showShiftTotals
    },
    items () {
      // Sempre usamos a lista local; no modo controlado ela espelha a prop `value`.
      return this.localItems
    },
    contextTeacher () {
      return this.teacher || this.localTeacher
    },
    contextWorkTime () {
      return this.workTime || this.localWorkTime
    },
    effectiveToken () {
      if (this.tokenTeacher || this.token) {
        return this.tokenTeacher || this.token
      }

      const routeToken = this.$route && this.$route.query ? this.$route.query.token : null
      return routeToken || null
    },
    disabled () {
      return !this.isAvailabilityEnabled && !this.release
    },
    isEditing () {
      return this.editingIndex !== null && this.editingIndex !== undefined
    },
    effectiveHeaders () {
      if (Array.isArray(this.headers) && this.headers.length > 0) {
        return this.headers
      }

      return [
        { text: 'Horário', number: 0 },
        { text: 'Domingo', number: 1 },
        { text: 'Segunda', number: 2 },
        { text: 'Terça', number: 3 },
        { text: 'Quarta', number: 4 },
        { text: 'Quinta', number: 5 },
        { text: 'Sexta', number: 6 },
        { text: 'Sábado', number: 7 },
      ]
    },
    daysOfWeekOptions () {
      return (this.effectiveHeaders || [])
        .filter(h => h.number && h.number !== 0)
        .map(h => ({ id: h.number, text: h.text }))
    },
    availabilityTypeOptions () {
      const options = [
        { id: 1, text: 'Disponível' },
      ]

      if (this.release) {
        options.push(
          { id: 2, text: 'Retirado' },
          { id: 3, text: 'Liberado' },
        )
      }

      if (this.showEnableActivityTime) {
        options.push({ id: 4, text: 'Hora Atividade' })
      }

      return options
    },
    effectiveShowByCampuses () {
      if (!this.autoShowByCampuses) {
        return !!this.showByCampuses
      }

      const settings = (this.contextTeacher && this.contextTeacher.realInstitution && this.contextTeacher.realInstitution.settings)
        ? this.contextTeacher.realInstitution.settings
        : (this.contextTeacher && this.contextTeacher.institution && this.contextTeacher.institution.settings)
          ? this.contextTeacher.institution.settings
          : null

      if (settings && settings.availability_by_campuses != null) {
        return !!settings.availability_by_campuses
      }

      return !!this.showByCampuses
    },
    campusesEffective () {
      if (Array.isArray(this.campuses) && this.campuses.length > 0) {
        return this.campuses
      }

      return this.localCampuses
    },
    campusOptions () {
      return (this.campusesEffective || []).map(c => ({
        id: c.id,
        text: `${c.initials} - ${c.name}`,
      }))
    },
    getColorFn () {
      if (typeof this.getColor === 'function') {
        return this.getColor
      }

      const colors = ['error', 'success', 'orange', 'blue', 'grey', 'purple']
      return (valor) => colors[valor]
    },
    getIconFn () {
      if (typeof this.getIcon === 'function') {
        return this.getIcon
      }

      return (valor) => {
        switch (valor) {
          case 0:
          case 2: return 'mdi-close-circle'
          case 4: return 'mdi-minus-circle-outline'
          default: return 'mdi-checkbox-marked-circle'
        }
      }
    },
    orderedItems () {
      const list = (this.items || []).map((item, idx) => ({
        ...item,
        __index: idx,
      }))

      const toMinutes = (t) => {
        const m = this.parseTimeToMinutes(t)
        return (m === null) ? 9999 : m
      }

      return list.sort((a, b) => {
        const da = parseInt(a.day_of_week_id)
        const db = parseInt(b.day_of_week_id)
        if (da !== db) return da - db

        const sa = toMinutes(a.hour_start)
        const sb = toMinutes(b.hour_start)
        if (sa !== sb) return sa - sb

        return a.__index - b.__index
      })
    },

    gridIntervalsByDayEffective () {
      const hasProp = this.gridIntervalsByDay && typeof this.gridIntervalsByDay === 'object'
        ? Object.keys(this.gridIntervalsByDay).some(k => Array.isArray(this.gridIntervalsByDay[k]) && this.gridIntervalsByDay[k].length > 0)
        : false

      if (hasProp) {
        return this.gridIntervalsByDay
      }

      const availability = this.getAvailabilityTeacher()
      const byDay = {}

      if (!availability || !Array.isArray(availability.availabilities)) {
        return byDay
      }

      availability.availabilities.forEach(a => {
        if (!a || !a.hour_id) return
        if (!a.day_of_week_id) return
        if (!a.hour_start || !a.hour_end) return

        const v = parseInt(a.availability_type_id)
        if (!v || ![1, 3, 4, 5].includes(v)) return

        const dayId = parseInt(a.day_of_week_id)
        const start = this.parseTimeToMinutes((a.hour_start || '').substring(0, 5))
        const end = this.parseTimeToMinutes((a.hour_end || '').substring(0, 5))
        if (start === null || end === null) return

        if (!byDay[dayId]) {
          byDay[dayId] = []
        }

        byDay[dayId].push({ start, end })
      })

      return byDay
    },

    // Intervalos vindos do backend (hour_id != null), usados para manter consistencia
    // quando o componente salva apenas as faixas personalizadas em uma tela que tambem
    // permite editar a grade semanal localmente.
    gridIntervalsByDayFromAvailability () {
      const availability = this.getAvailabilityTeacher()
      const byDay = {}

      if (!availability || !Array.isArray(availability.availabilities)) {
        return byDay
      }

      availability.availabilities.forEach(a => {
        if (!a || !a.hour_id) return
        if (!a.day_of_week_id) return
        if (!a.hour_start || !a.hour_end) return

        const v = parseInt(a.availability_type_id)
        if (!v || ![1, 3, 4, 5].includes(v)) return

        const dayId = parseInt(a.day_of_week_id)
        const start = this.parseTimeToMinutes((a.hour_start || '').substring(0, 5))
        const end = this.parseTimeToMinutes((a.hour_end || '').substring(0, 5))
        if (start === null || end === null) return

        if (!byDay[dayId]) {
          byDay[dayId] = []
        }

        byDay[dayId].push({ start, end })
      })

      return byDay
    },

    shiftTotals () {
      const counters = {
        total: 0,
        morning: 0,
        evening: 0,
        night: 0,
      }

      const grid = this.gridIntervalsByDayEffective || {}
      Object.keys(grid).forEach(dayId => {
        ;(grid[dayId] || []).forEach(it => {
          this.sumIntervalIntoShiftCounters(it.start, it.end, counters)
        })
      })

      ;(this.items || []).forEach(item => {
        if (!item) return
        const v = parseInt(item.availability_type_id)
        if (!v || ![1, 3, 4, 5].includes(v)) return
        const start = this.parseTimeToMinutes((item.hour_start || '').substring(0, 5))
        const end = this.parseTimeToMinutes((item.hour_end || '').substring(0, 5))
        this.sumIntervalIntoShiftCounters(start, end, counters)
      })

      return {
        total: parseFloat(counters.total.toFixed(2)),
        morning: parseFloat(counters.morning.toFixed(2)),
        evening: parseFloat(counters.evening.toFixed(2)),
        night: parseFloat(counters.night.toFixed(2)),
      }
    },

    manualChParsed () {
      const parse = (value) => {
        if (value === null || value === undefined) {
          return null
        }
        if (typeof value === 'number') {
          return isNaN(value) ? null : value
        }
        if (typeof value !== 'string') {
          return null
        }

        const raw = value.trim()
        if (!raw) {
          return null
        }

        const normalized = raw.replace(',', '.')
        const n = parseFloat(normalized)
        if (isNaN(n)) {
          return null
        }
        return n
      }

      return {
        morning: parse(this.manualCh.morning),
        evening: parse(this.manualCh.evening),
        night: parse(this.manualCh.night),
      }
    },

    shiftTotalsEffective () {
      const auto = this.shiftTotals
      const o = this.manualChParsed

      const morning = (o.morning !== null) ? o.morning : auto.morning
      const evening = (o.evening !== null) ? o.evening : auto.evening
      const night = (o.night !== null) ? o.night : auto.night

      return {
        morning,
        evening,
        night,
        total: morning + evening + night,
      }
    },

    shiftTotalsForPayload () {
      const counters = {
        total: 0,
        morning: 0,
        evening: 0,
        night: 0,
      }

      // No modo controlado (usado dentro da pagina), usamos os intervalos do backend
      // para evitar enviar totais inconsistentes com a grade ainda nao salva.
      const grid = this.isControlledModel ? (this.gridIntervalsByDayFromAvailability || {}) : (this.gridIntervalsByDayEffective || {})
      Object.keys(grid).forEach(dayId => {
        ;(grid[dayId] || []).forEach(it => {
          this.sumIntervalIntoShiftCounters(it.start, it.end, counters)
        })
      })

      ;(this.items || []).forEach(item => {
        if (!item) return
        const v = parseInt(item.availability_type_id)
        if (!v || ![1, 3, 4, 5].includes(v)) return
        const start = this.parseTimeToMinutes((item.hour_start || '').substring(0, 5))
        const end = this.parseTimeToMinutes((item.hour_end || '').substring(0, 5))
        this.sumIntervalIntoShiftCounters(start, end, counters)
      })

      return {
        total: parseFloat(counters.total.toFixed(2)),
        morning: parseFloat(counters.morning.toFixed(2)),
        evening: parseFloat(counters.evening.toFixed(2)),
        night: parseFloat(counters.night.toFixed(2)),
      }
    },

    shiftTotalsForPayloadEffective () {
      const auto = this.shiftTotalsForPayload
      const o = this.manualChParsed

      const morning = (o.morning !== null) ? o.morning : auto.morning
      const evening = (o.evening !== null) ? o.evening : auto.evening
      const night = (o.night !== null) ? o.night : auto.night

      return {
        morning,
        evening,
        night,
        total: morning + evening + night,
      }
    },

    shiftTotalsDisplay () {
      return {
        total: this.shiftTotalsEffective.total.toFixed(2),
        morning: this.shiftTotalsEffective.morning.toFixed(2),
        evening: this.shiftTotalsEffective.evening.toFixed(2),
        night: this.shiftTotalsEffective.night.toFixed(2),
      }
    },
  },
  methods: {
    ...mapActions('availability', ['ActionAvailabilityCreate', 'ActionAvailabilityUpdate', 'ActionGetWorkTimeByToken']),
    ...mapActions('teacher', ['ActionFindTeacherByToken', 'ActionFindTeacher']),
    ...mapActions('campus', ['ActionFindCampuses']),
    ...mapActions('auth', ['ActionSetToken']),
    ...mapActions('worktime', ['ActionShowWorkTime']),

    async init () {
      if (!this.autoload) {
        return
      }

      // Quando o componente esta sendo controlado via v-model/@input,
      // ele deve funcionar como editor puro e nao exigir/forcar carregamento de contexto.
      // O contexto so e necessario para salvar (modo standalone).
      if (this.isControlledModel) {
        return
      }

      if (!this.isControlledModel) {
        this.hydrateFromContext()
      }

      if (!this.contextTeacher || !this.contextWorkTime) {
        if (this.effectiveToken) {
          await this.loadByToken(this.effectiveToken)
        } else if (this.teacherId && this.workTimeId) {
          await this.loadByIds(this.teacherId, this.workTimeId)
        } else {
          this.showSnack('Informe um token (prop `token` ou querystring `?token=`) ou os ids (`teacherId` e `workTimeId`) para carregar o contexto.', true)
        }
      }

      if (this.effectiveShowByCampuses && (!this.campusesEffective || this.campusesEffective.length === 0)) {
        await this.loadCampusesIfNeeded()
      }
    },

    hydrateFromContext () {
      if (this.isControlledModel) {
        return
      }

      if (this.localItems && this.localItems.length > 0) {
        return
      }

      const availability = this.getAvailabilityTeacher()
      const custom = (availability && Array.isArray(availability.availabilities))
        ? availability.availabilities
          .filter(a => a && !a.hour_id)
          .map(a => ({
            day_of_week_id: a.day_of_week_id,
            hour_start: (a.hour_start || '').substring(0, 5),
            hour_end: (a.hour_end || '').substring(0, 5),
            availability_type_id: parseInt(a.availability_type_id),
            campuses: (this.effectiveShowByCampuses && Array.isArray(a.campuses))
              ? a.campuses.map(c => (c && c.id) ? c.id : c).filter(Boolean)
              : [],
            hour_id: null,
          }))
        : []

      this.localItems = custom
    },

    async loadCampusesIfNeeded () {
      if (!this.effectiveShowByCampuses) {
        return
      }

      if (this.campusesEffective && this.campusesEffective.length > 0) {
        return
      }

      const campusRes = await this.ActionFindCampuses({ columns: 'id,code,initials,name' })
      if (campusRes && campusRes.success) {
        this.localCampuses = campusRes.data
      }
    },

    async loadByToken (token) {
      this.saving = true
      try {
        await this.ActionSetToken(token)

        const workTimeRes = await this.ActionGetWorkTimeByToken({ token, with: 'term' })
        if (workTimeRes && workTimeRes.success) {
          this.localWorkTime = workTimeRes.data
        }

        if (!this.localWorkTime || !this.localWorkTime.id) {
          this.showSnack('Período de disponibilidade não encontrado.', true)
          return
        }

        const teacherPayload = {
          token,
          with: 'institution.termOptions,institution.hourGroups,emails,contracts.position,availability.availabilities,campuses',
          withContract: true,
          filter_relation_withs: [
            { name: 'confirmation', key: 'work_time_id', value: this.localWorkTime.id },
            { name: 'availability', key: 'work_time_id', value: this.localWorkTime.id },
          ],
        }

        const teacherRes = await this.ActionFindTeacherByToken(teacherPayload)
        if (teacherRes && teacherRes.success) {
          this.localTeacher = teacherRes.data
        } else {
          this.showSnack('Dados do professor não encontrados.', true)
          return
        }

        await this.loadCampusesIfNeeded()

        if (this.localTeacher && Array.isArray(this.localTeacher.campuses) && this.localTeacher.campuses.length > 0) {
          const mapped = this.localTeacher.campuses
            .map(c => (c && c.id) ? c.id : c)
            .filter(Boolean)
          this.localCampuses = (this.localCampuses || []).filter(c => mapped.includes(c.id))
        }

        this.hydrateFromContext()
      } catch (error) {
        const text = (error && error.message) ? error.message : 'Falha ao carregar contexto por token.'
        this.showSnack(text, true)
      } finally {
        this.saving = false
      }
    },

    async loadByIds (teacherId, workTimeId) {
      this.saving = true
      try {
        const wtId = parseInt(workTimeId)
        const tId = parseInt(teacherId)

        if (!wtId || !tId) {
          this.showSnack('Contexto insuficiente para carregar a disponibilidade.', true)
          return
        }

        const wtRes = await this.ActionShowWorkTime({ id: wtId })
        if (wtRes && wtRes.success) {
          this.localWorkTime = wtRes.data
        }

        const teacherPayload = {
          id: tId,
          with: 'institution.termOptions,institution.hourGroups,emails,contracts.position,availability.availabilities,campuses',
          withContract: true,
          filter_relation_withs: [
            { name: 'confirmation', key: 'work_time_id', value: wtId },
            { name: 'availability', key: 'work_time_id', value: wtId },
          ],
        }

        const teacherRes = await this.ActionFindTeacher(teacherPayload)
        if (teacherRes && teacherRes.success) {
          this.localTeacher = teacherRes.data
        } else {
          this.showSnack('Dados do professor não encontrados.', true)
          return
        }

        await this.loadCampusesIfNeeded()

        if (this.localTeacher && Array.isArray(this.localTeacher.campuses) && this.localTeacher.campuses.length > 0) {
          const mapped = this.localTeacher.campuses
            .map(c => (c && c.id) ? c.id : c)
            .filter(Boolean)
          this.localCampuses = (this.localCampuses || []).filter(c => mapped.includes(c.id))
        }

        this.hydrateFromContext()
      } catch (error) {
        const text = (error && error.message) ? error.message : 'Falha ao carregar contexto por id.'
        this.showSnack(text, true)
      } finally {
        this.saving = false
      }
    },

    showSnack (text, isError) {
      this.snackbarColor = isError ? 'error' : 'success'
      this.snackbarText = text
      this.snackbar = true
    },

    parseTimeToMinutes (value) {
      if (!value || typeof value !== 'string') {
        return null
      }

      const match = value.trim().match(/^([01]\d|2[0-3]):([0-5]\d)$/)
      if (!match) {
        return null
      }

      const h = parseInt(match[1], 10)
      const m = parseInt(match[2], 10)
      return (h * 60) + m
    },

    getShiftByStartMinutes (startMinutes) {
      // Mantido por compatibilidade; a soma por turno nao deve depender apenas do inicio.
      if (startMinutes < (13 * 60)) {
        return 'morning'
      }

      if (startMinutes < (19 * 60)) {
        return 'evening'
      }

      return 'night'
    },

    sumIntervalIntoShiftCounters (startMinutes, endMinutes, counters) {
      if (startMinutes === null || endMinutes === null) {
        return
      }

      if (startMinutes >= endMinutes) {
        return
      }

      const totalHours = (endMinutes - startMinutes) / 60

      // Distribui corretamente quando a faixa cruza turnos.
      const MORNING_END = 13 * 60
      const EVENING_END = 19 * 60

      const morningStart = startMinutes
      const morningEnd = Math.min(endMinutes, MORNING_END)
      if (morningStart < morningEnd) {
        counters.morning += (morningEnd - morningStart) / 60
      }

      const eveningStart = Math.max(startMinutes, MORNING_END)
      const eveningEnd = Math.min(endMinutes, EVENING_END)
      if (eveningStart < eveningEnd) {
        counters.evening += (eveningEnd - eveningStart) / 60
      }

      const nightStart = Math.max(startMinutes, EVENING_END)
      const nightEnd = endMinutes
      if (nightStart < nightEnd) {
        counters.night += (nightEnd - nightStart) / 60
      }

      if (counters.total != null) {
        counters.total += totalHours
      }
    },

    intervalsOverlap (aStart, aEnd, bStart, bEnd) {
      return aStart < bEnd && bStart < aEnd
    },
    resetForm () {
      this.editingIndex = null
      this.formModel = {
        day_of_week_id: null,
        hour_start: '',
        hour_end: '',
        availability_type_id: 1,
        campuses: [],
      }
    },
    validateCandidate (candidate, excludeIndex = null) {
      if (!candidate.day_of_week_id) {
        return { ok: false, message: 'Informe o dia da semana.' }
      }

      const start = this.parseTimeToMinutes(candidate.hour_start)
      const end = this.parseTimeToMinutes(candidate.hour_end)

      if (start === null || end === null) {
        return { ok: false, message: 'Informe os horários no formato HH:mm.' }
      }

      if (start >= end) {
        return { ok: false, message: 'O horário de início deve ser menor que o horário de fim.' }
      }

      const dayId = parseInt(candidate.day_of_week_id)

      for (let i = 0; i < (this.items || []).length; i++) {
        if (excludeIndex !== null && i === excludeIndex) {
          continue
        }

        const item = this.items[i]
        if (parseInt(item.day_of_week_id) !== dayId) {
          continue
        }

        const iStart = this.parseTimeToMinutes(item.hour_start)
        const iEnd = this.parseTimeToMinutes(item.hour_end)
        if (iStart === null || iEnd === null) {
          continue
        }

        if (this.intervalsOverlap(start, end, iStart, iEnd)) {
          return { ok: false, message: 'A faixa personalizada sobrepõe outra faixa do mesmo dia.' }
        }
      }

      const gridIntervals = (this.gridIntervalsByDayEffective && this.gridIntervalsByDayEffective[dayId]) ? this.gridIntervalsByDayEffective[dayId] : []
      for (let j = 0; j < gridIntervals.length; j++) {
        const gi = gridIntervals[j]
        if (this.intervalsOverlap(start, end, gi.start, gi.end)) {
          return { ok: false, message: 'A faixa personalizada sobrepõe um horário selecionado na grade do mesmo dia.' }
        }
      }

      return { ok: true }
    },

    validateAll () {
      for (let i = 0; i < (this.items || []).length; i++) {
        const item = this.items[i]
        const res = this.validateCandidate(item, i)
        if (!res.ok) {
          return res
        }
      }

      return { ok: true }
    },

    getAvailabilityTeacher () {
      const t = this.contextTeacher
      if (t && Array.isArray(t.availabilities) && t.availabilities.length > 0) {
        if (this.contextWorkTime && this.contextWorkTime.id) {
          const found = t.availabilities.find(a => a && parseInt(a.work_time_id) === parseInt(this.contextWorkTime.id))
          if (found) {
            return found
          }
        }

        return t.availabilities[0]
      }
      return null
    },

    getAvailabilityObject () {
      return {
        id: 0,
        teacher_id: this.contextTeacher ? this.contextTeacher.id : 0,
        work_time_id: this.contextWorkTime ? this.contextWorkTime.id : 0,
        status_availability_id: 2,
        term_id: null,
        agree_term: null,
        term_option_id: null,
        is_priority: 0,
        requires_complementary: 0,
        max_workload: 0,
        note: '',
        token: '',
        morning_availability: 0,
        evening_availability: 0,
        night_availability: 0,
        digital_availability: 0,
        digital_availability_teacher: 0,
        activity_availability: 0,
        availabilities: [],
      }
    },

    getCustomAvailabilitiesPayload (availabilityId) {
      return (this.items || []).map(item => ({
        availability_id: availabilityId,
        hour_id: null,
        hour_start: item.hour_start,
        hour_end: item.hour_end,
        day_of_week_id: item.day_of_week_id,
        availability_type_id: item.availability_type_id,
        campuses: this.effectiveShowByCampuses ? (item.campuses || []) : [],
      }))
    },

    buildPayload () {
      let availability = this.getAvailabilityTeacher()
      if (!availability) {
        availability = this.getAvailabilityObject()
      }

      // evitar mutacao direta em prop/objeto do store
      availability = {
        ...availability,
        availabilities: Array.isArray(availability.availabilities) ? [...availability.availabilities] : [],
      }

      availability.isToken = this.effectiveToken != null
      if (this.effectiveToken) {
        availability.token = this.effectiveToken
      }

      if (this.release) {
        availability.alter_from_user = true
      }

      availability.status_availability_id = this.status

      // manter horarios cadastrados (hour_id != null) e trocar somente as faixas
      const existingHourAvailabilities = (availability.availabilities || []).filter(a => !!a.hour_id)
      const custom = this.getCustomAvailabilitiesPayload(availability.id)
      availability.availabilities = existingHourAvailabilities.concat(custom)

      availability.morning_availability = this.shiftTotalsForPayloadEffective.morning
      availability.evening_availability = this.shiftTotalsForPayloadEffective.evening
      availability.night_availability = this.shiftTotalsForPayloadEffective.night

      const digital = parseFloat(this.digitalAvailability)
      availability.digital_availability = isNaN(digital) ? 0 : digital

      // Quando estiver criando a disponibilidade por aqui, definimos um max_workload coerente
      // com a selecao atual (grade + faixas) + CH digital.
      if (!availability.id || availability.id === 0) {
        availability.max_workload = parseFloat((this.shiftTotalsForPayloadEffective.total + availability.digital_availability).toFixed(2))
      }

      return availability
    },

    upsertAvailabilityOnTeacher (teacher, availability) {
      if (!teacher || !availability || !availability.id) {
        return
      }

      if (!Array.isArray(teacher.availabilities)) {
        this.$set(teacher, 'availabilities', [])
      }

      const idx = teacher.availabilities.findIndex(a => a && parseInt(a.id) === parseInt(availability.id))
      if (idx >= 0) {
        teacher.availabilities.splice(idx, 1, availability)
      } else {
        teacher.availabilities.push(availability)
      }
    },

    async save () {
      if (this.disabled) {
        return
      }

      if (!this.effectiveShowSave) {
        return
      }

      if (!this.contextTeacher || !this.contextTeacher.id || !this.contextWorkTime || !this.contextWorkTime.id) {
        const msg = 'Contexto insuficiente para salvar a disponibilidade.'
        this.showSnack(msg, true)
        this.$emit('error', msg)
        return
      }

      const validation = this.validateAll()
      if (!validation.ok) {
        this.showSnack(validation.message, true)
        this.$emit('error', validation.message)
        return
      }

      const payload = this.buildPayload()

      // Se o periodo exige concordancia de termo, e nao temos os dados para isso,
      // avisamos com clareza (o fluxo completo existe na pagina principal).
      if (!this.release && this.contextWorkTime && this.contextWorkTime.show_agree_term && this.contextWorkTime.term) {
        const isRequired = !!this.contextWorkTime.term.is_required
        if (payload.id === 0) {
          if (isRequired && !payload.agree_term) {
            this.showSnack('Para salvar pela primeira vez, confirme o termo na tela principal de disponibilidade.', true)
            return
          }
          if (!isRequired && !payload.term_option_id) {
            this.showSnack('Para salvar pela primeira vez, selecione a opcao do termo na tela principal de disponibilidade.', true)
            return
          }
        }
      }

      const method = (payload.id && payload.id !== 0) ? 'ActionAvailabilityUpdate' : 'ActionAvailabilityCreate'

      this.saving = true
      try {
        const res = await this[method](payload)

        if (res && res.success === false) {
          const msg = res.message || 'Falha ao salvar disponibilidade.'
          this.showSnack(msg, true)
          this.$emit('error', msg)
          return
        }

        if (res && res.data) {
          this.upsertAvailabilityOnTeacher(this.contextTeacher, res.data)
          if (this.localTeacher && this.localTeacher !== this.contextTeacher) {
            this.upsertAvailabilityOnTeacher(this.localTeacher, res.data)
          }
        }

        this.showSnack((res && res.message) ? res.message : 'Disponibilidade salva com sucesso.', false)
        this.$emit('saved', res)
      } catch (error) {
        const text = (error && error.message) ? error.message : 'Falha ao salvar disponibilidade.'
        this.showSnack(text, true)
        this.$emit('error', text)
      } finally {
        this.saving = false
      }

    },
    onAddOrUpdate () {
      if (this.disabled) {
        return
      }

      const candidate = {
        day_of_week_id: this.formModel.day_of_week_id,
        hour_start: (this.formModel.hour_start || '').trim(),
        hour_end: (this.formModel.hour_end || '').trim(),
        availability_type_id: parseInt(this.formModel.availability_type_id),
        campuses: Array.isArray(this.formModel.campuses) ? [...this.formModel.campuses] : [],
        hour_id: null,
      }

      const exclude = this.isEditing ? this.editingIndex : null
      const validation = this.validateCandidate(candidate, exclude)
      if (!validation.ok) {
        this.showSnack(validation.message, true)
        this.$emit('error', validation.message)
        return
      }

      const list = [...(this.items || [])]
      if (this.isEditing) {
        list.splice(this.editingIndex, 1, candidate)
      } else {
        list.push(candidate)
      }

      if (this.isControlledModel) {
        this.localItems = list
        this.$emit('input', list)
      } else {
        this.localItems = list
      }
      this.resetForm()

      this.$nextTick(() => {
        this.$emit('changed')
      })
    },
    onReset () {
      this.resetForm()
    },
    edit (index) {
      const item = (this.items || [])[index]
      if (!item) {
        return
      }

      this.editingIndex = index
      this.formModel = {
        day_of_week_id: item.day_of_week_id,
        hour_start: item.hour_start,
        hour_end: item.hour_end,
        availability_type_id: item.availability_type_id,
        campuses: Array.isArray(item.campuses) ? [...item.campuses] : [],
      }
    },
    remove (index) {
      const list = [...(this.items || [])]
      list.splice(index, 1)

      if (this.isControlledModel) {
        this.localItems = list
        this.$emit('input', list)
      } else {
        this.localItems = list
      }

      if (this.editingIndex === index) {
        this.resetForm()
      }

      this.$nextTick(() => {
        this.$emit('changed')
      })
    },
  },
}
</script>
